import { z } from 'zod'

import { SpecGroupSchema } from './catalog'
import { LocalizedMediaSchema, LocalizedSchema, PublishStatusSchema, SlugSchema } from './common'

// Material and finish swatches: the old site's reference library behind /materials, /finishes
// and the swatch strips on product, category and industry pages.

export const SWATCH_KINDS = ['material', 'finish'] as const
export const SwatchKindSchema = z.enum(SWATCH_KINDS)
export type SwatchKind = z.infer<typeof SwatchKindSchema>

export const SwatchInputSchema = z.object({
  slug: SlugSchema,
  kind: SwatchKindSchema,
  /** Key of the group it sits under on /materials or /finishes (groups are defined on that page's Swatches block). */
  category: z.string().max(60).regex(/^[a-z0-9-]*$/, 'Lowercase letters, numbers and dashes'),
  sortOrder: z.number().int().min(0).max(10_000),
  status: PublishStatusSchema,
  title: LocalizedSchema,
  description: LocalizedSchema,
  /** Longer buyer's note under the description. */
  note: LocalizedSchema,
  image: LocalizedMediaSchema,
  /** Optional link to a guide article ("Read the foil stamping guide"). */
  guideHref: z.string().max(500),
  guideLabel: LocalizedSchema,
  /** Packaging spec groups this swatch applies to; landing pages list the swatches for their own groups. */
  groups: z.array(SpecGroupSchema).max(8),
  /** Rank (0 = first) per spec group in which it is a recommended pick, listed before the rest. */
  featured: z.partialRecord(SpecGroupSchema, z.number().int().min(0).max(99)),
})
export const SwatchSchema = SwatchInputSchema.extend({ id: z.string(), createdAt: z.string(), updatedAt: z.string() })
export type SwatchInput = z.infer<typeof SwatchInputSchema>
export type Swatch = z.infer<typeof SwatchSchema>

/**
 * Site-wide reference copy shown on landing pages: "Terms for this specification" (per spec
 * group; none listed = every group) and the "What drives the cost" cells.
 */
export const SpecReferenceSchema = z.object({
  terms: z.array(z.object({ label: LocalizedSchema, value: LocalizedSchema, groups: z.array(SpecGroupSchema).max(8) })).max(30),
  costDrivers: z.array(z.object({ name: LocalizedSchema, description: LocalizedSchema })).max(12),
})
export type SpecReference = z.infer<typeof SpecReferenceSchema>
export const emptySpecReference = (): SpecReference => ({ terms: [], costDrivers: [] })
